import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Compact question form for any published material.
 * Auto-captures material context on the backend.
 * Renders as a dialog on all viewports; on narrow screens Radix Dialog
 * degrades gracefully to a full-height sheet-like feel.
 */
export default function QuestionDialog({ open, onOpenChange, material, onCreated }) {
  const nav = useNavigate();
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const close = () => { if (!sending) { setBody(""); onOpenChange(false); } };

  const send = async () => {
    const trimmed = body.trim();
    if (!trimmed) {
      toast.error("Напишите Ваше сообщение");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/account/requests", { material_id: material.id, body: trimmed });
      toast.success("Обращение отправлено");
      setBody("");
      onOpenChange(false);
      if (onCreated) onCreated(data);
      // Deep-link straight into the new request
      setTimeout(() => nav(`/account?tab=requests&open=${data.id}`), 300);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="question-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-espresso">Задать вопрос</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            По материалу «{material?.title || "…"}»
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">
            Ваше сообщение
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            data-testid="question-body-input"
            className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm font-serif text-espresso focus:outline-none focus:ring-2 focus:ring-espresso/20"
            placeholder="Напишите Ваш вопрос, уточнение или обратную связь…"
            disabled={sending}
          />
        </div>
        <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            className="btn-pill min-h-[40px]"
            onClick={close}
            disabled={sending}
            data-testid="question-cancel-button"
          >
            Отмена
          </Button>
          <Button
            type="button"
            onClick={send}
            disabled={sending || !body.trim()}
            className="btn-pill min-h-[40px] bg-[#D9E4C8] hover:bg-[#CFDCBB] text-[#3B4A2A] border border-[#B8C7A0] shadow-none font-medium"
            data-testid="question-send-button"
          >
            {sending ? "Отправляем…" : "Отправить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
